import { useClerk } from "@clerk/clerk-react";
import { AxiosError } from "axios";
import { useEffect } from "react";
import { Button } from "../ui/button";

/**
 * Why the CRM could not be opened, when /internal/access/me does not answer.
 *
 * There is no second opinion to fall back on: roles and area grants live in our
 * database and nowhere else, so a failure here is not "assume the least" but
 * "we do not know". The point of naming the four cases is that three of them
 * need completely different things from the person in front of the screen —
 * sign in again, wait, or fetch an administrator — and a single "no access"
 * message sends all three to the wrong place.
 */
export type AccessFailure =
  | "signed-out"
  | "not-set-up"
  | "no-access"
  | "unavailable";

/**
 * `/me` is area-exempt and asks only for `read`, so the answers it can give are
 * few and known. 401 is the token: expired, invalid, or outliving an account
 * that has been removed. 403 splits on the code RolesGuard sends — no staff row
 * yet, which fixes itself, versus a role that genuinely grants nothing, which
 * does not. Everything else, including a 503 and a request that never arrived,
 * is us being unable to answer.
 */
export function classifyAccessError(error: unknown): AccessFailure {
  if (!(error instanceof AxiosError)) return "unavailable";

  const status = error.response?.status;
  const code = (error.response?.data as { code?: string } | undefined)?.code;

  if (status === 401) return "signed-out";
  // 404 is the same situation reported by the endpoint itself rather than by
  // the guard, which is what happens under the local dev auth bypass.
  if (status === 404) return "not-set-up";
  if (status === 403) {
    return code === "NO_STAFF_RECORD" ? "not-set-up" : "no-access";
  }
  return "unavailable";
}

/** Separated from the markup so the wording can be asserted directly. */
export function describeAccessFailure(failure: AccessFailure): {
  title: string;
  body: string;
  canRetry: boolean;
} {
  switch (failure) {
    case "not-set-up":
      return {
        title: "Your account is still being set up",
        body: "This usually takes a few seconds. If it does not clear, ask an administrator to check that your staff record was created.",
        canRetry: true,
      };
    case "no-access":
      return {
        title: "Your role has no access to the CRM",
        body: "Ask an administrator to grant your role access on the Manage Access screen.",
        canRetry: false,
      };
    case "signed-out":
      return {
        title: "Signing you out",
        body: "Your session is no longer valid.",
        canRetry: false,
      };
    default:
      return {
        title: "Couldn't load your access",
        body: "We could not reach the server to check what you can open. Nothing is wrong with your account.",
        canRetry: true,
      };
  }
}

/**
 * Rendered in place of the whole CRM. Deliberately not a page inside the app:
 * without an answer from /me there is no sidebar to show, no landing page to
 * pick, and every gate below would fail closed anyway.
 */
export default function AccessUnavailable({
  failure,
  onRetry,
  isRetrying,
}: {
  failure: AccessFailure;
  onRetry: () => void;
  isRetrying?: boolean;
}) {
  const { signOut } = useClerk();
  const { title, body, canRetry } = describeAccessFailure(failure);

  // A dead token cannot be retried into life, and every request the app makes
  // would carry it. Clear the session and let App send them to sign-in.
  useEffect(() => {
    if (failure === "signed-out") void signOut();
  }, [failure, signOut]);

  return (
    <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-lg font-medium">{title}</h1>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
      {failure !== "signed-out" && (
        <div className="mt-2 flex items-center gap-2">
          {canRetry && (
            <Button size="sm" onClick={onRetry} disabled={isRetrying}>
              {isRetrying ? "Checking…" : "Try again"}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      )}
    </div>
  );
}
